import { useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/lib/platform';

import { ticketsApi } from '@/lib/api';
import { ridesApi } from '@/lib/rides-api';
import { passesApi } from '@/lib/passes-api';
import { queryKeys, rideKeys, passKeys } from '@/lib/query-keys';
import { formatDateTime } from '@/lib/format';
import {
  formatIgmRefundAmount,
  igmActionLabel,
  igmProviderLabel,
  igmResolutionActionLabel,
} from '@/lib/igm-labels';
import { Badge } from '@/components/shared/badge';
import { ErrorState } from '@/components/shared/error-state';
import { Skeleton } from '@/components/shared/skeleton';
import { Screen } from '@/components/chrome';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_authed/support/$category/$issueId')({
  component: IssueDetailPage,
});

interface IssueView {
  description: string;
  category?: string;
  createdAt?: string;
  lastAction?: string;
  actions: { id?: string; code?: string; shortDesc?: string; createdAt?: string }[];
  level: string;
  status: string;
  resolutions: {
    id?: string;
    code?: string;
    shortDesc?: string;
    longDesc?: string;
    actionTriggered?: string;
    refundAmount?: string;
    resolutionProvider?: string;
  }[];
}

const MAX_IMAGES = 3;
const ACCEPT = 'image/png,image/jpeg,image/webp';

function IssueDetailPage() {
  const { category: rawCategory, issueId } = Route.useParams();
  const isIGMV1 = rawCategory === 'bus' || rawCategory === 'metro' || rawCategory === 'rides';
  const navigate = useNavigate();
  const api =
    rawCategory === 'rides' ? ridesApi : rawCategory === 'passes' ? passesApi : ticketsApi;
  const keys = rawCategory === 'rides' ? rideKeys : rawCategory === 'passes' ? passKeys : queryKeys;
  const supportLabel =
    rawCategory === 'rides'
      ? 'Ride support'
      : rawCategory === 'passes'
        ? 'Entry-pass support'
        : rawCategory === 'bus'
          ? 'Bus support'
          : 'Metro support';
  const qc = useQueryClient();

  const [info, setInfo] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionNote, setActionNote] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  // ---------- Rejection‑specific state ----------
  const [rejectInfo, setRejectInfo] = useState('');
  const [rejectImages, setRejectImages] = useState<string[]>([]);
  const [rejectUploading, setRejectUploading] = useState(false);
  const rejectFileRef = useRef<HTMLInputElement>(null);
  const [showRejectPanel, setShowRejectPanel] = useState(false);

  const {
    data: issue,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<IssueView>({
    queryKey: keys.issue(issueId),
    queryFn: async () => {
      const d = await api.issue(issueId);
      return {
        description: d.description,
        category: d.category,
        createdAt: d.createdAt,
        lastAction: d.lastAction,
        actions: d.actions ?? [],
        level: d.level,
        status: d.status,
        resolutions: d.resolutions ?? [],
      };
    },
    refetchInterval: (query) => (query.state.data?.status === 'CLOSED' ? false : 3_000),
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: keys.issue(issueId) });
    void qc.invalidateQueries({ queryKey: keys.allIssues() });
  }
  function startAction() {
    setActionError('');
    setActionNote('');
  }

  async function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (fileRef.current) fileRef.current.value = '';
    if (files.length === 0) return;
    startAction();
    setUploading(true);
    try {
      const room = MAX_IMAGES - images.length;
      const next: string[] = [];
      for (const file of files.slice(0, room)) {
        const url = await api.uploadIssueAttachment(file);
        next.push(url);
      }
      setImages((cur) => [...cur, ...next]);
    } catch (err) {
      setActionError(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  // ---------------------------------------------------------------------
  // Rejection‑specific file picker (mirrors onPickFiles)
  async function onPickRejectFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (rejectFileRef.current) rejectFileRef.current.value = '';
    if (files.length === 0) return;
    startAction();
    setRejectUploading(true);
    try {
      const room = MAX_IMAGES - rejectImages.length;
      const next: string[] = [];
      for (const file of files.slice(0, room)) {
        const url = await api.uploadIssueAttachment(file);
        next.push(url);
      }
      setRejectImages((cur) => [...cur, ...next]);
    } catch (err) {
      setActionError(getErrorMessage(err));
    } finally {
      setRejectUploading(false);
    }
  }

  const updateMut = useMutation({
    mutationFn: () =>
      api.updateIssue(issueId, {
        actionCode: 'INFO_PROVIDED',
        shortDesc: info.trim() || 'Information and image provided',
        images: images.length ? images : undefined,
      }),
    onMutate: startAction,
    onSuccess: () => {
      setInfo('');
      setImages([]);
      setActionNote('Information sent to the operator.');
      invalidate();
    },
    onError: (e) => setActionError(getErrorMessage(e)),
  });
  const escalateMut = useMutation({
    mutationFn: () => api.escalateIssue(issueId, { shortDesc: 'Escalating to grievance' }),
    onMutate: startAction,
    onSuccess: () => {
      setActionNote('Escalated to a grievance.');
      invalidate();
    },
    onError: (e) => setActionError(getErrorMessage(e)),
  });
  const closeMut = useMutation({
    mutationFn: (rating: string) =>
      api.closeIssue(issueId, {
        rating,
        ...(isIGMV1 ? { igmVersion: '1.0.0' as const } : {}),
      }),
    onMutate: startAction,
    onSuccess: () => {
      setActionNote('Close request sent.');
      qc.setQueryData<IssueView>(keys.issue(issueId), (current) =>
        current ? { ...current, status: 'CLOSED', lastAction: 'CLOSED' } : current
      );
      invalidate();
    },
    onError: (e) => setActionError(getErrorMessage(e)),
  });
  const resolveMut = useMutation({
    // `params` includes `accepted` flag, optional images and comment for rejection
    mutationFn: (params: { accepted: boolean; images?: string[]; comment?: string }) => {
      const body: { actionCode: string; shortDesc: string; images?: string[] } = {
        actionCode: params.accepted ? 'RESOLUTION_ACCEPTED' : 'RESOLUTION_REJECTED',
        shortDesc: params.accepted
          ? 'Resolution accepted by customer'
          : params.comment?.trim() || 'Resolution rejected by customer',
      };
      if (!params.accepted && params.images?.length) {
        body.images = params.images;
      }
      return api.updateIssue(issueId, body);
    },
    onMutate: startAction,
    onSuccess: (_d, params) => {
      setActionNote(
        params.accepted
          ? 'Resolution accepted.'
          : 'Resolution rejected — the operator will follow up.'
      );
      // Close rejection panel after a successful rejection
      if (!params.accepted) setShowRejectPanel(false);
      invalidate();
    },
    onError: (e) => setActionError(getErrorMessage(e)),
  });

  const back = () => void navigate({ to: '/support' });

  if (isLoading) {
    return (
      <Screen title="Issue" onBack={back}>
        <Skeleton w="100%" h={192} r={22} />
      </Screen>
    );
  }
  if (isError || !issue) {
    return (
      <Screen title="Issue" onBack={back}>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Screen>
    );
  }

  const actionCodes = issue.actions.map((a) => a.code);
  const status = issue.status.trim().toUpperCase();
  const closed = status === 'CLOSED';
  const acceptedActionIndex = actionCodes.lastIndexOf('RESOLUTION_ACCEPTED');
  const rejectedActionIndex = actionCodes.lastIndexOf('RESOLUTION_REJECTED');
  const proposedActionIndex = actionCodes.lastIndexOf('RESOLUTION_PROPOSED');
  const resolvedActionIndex = actionCodes.lastIndexOf('RESOLVED');
  const sellerResolved = status === 'RESOLVED' || resolvedActionIndex !== -1;

  // Customer has accepted if RESOLUTION_ACCEPTED is present and newer than any rejection
  const hasAccepted =
    !closed &&
    (acceptedActionIndex !== -1 && acceptedActionIndex > rejectedActionIndex);

  // Customer has rejected if RESOLUTION_REJECTED is present and newer than any acceptance and latest proposal
  const hasRejected =
    !closed &&
    !hasAccepted &&
    (rejectedActionIndex !== -1 &&
      rejectedActionIndex > acceptedActionIndex &&
      rejectedActionIndex >= proposedActionIndex);

  // Resolution is pending customer decision if a proposal exists and customer has not accepted or rejected it
  const resolutionPending =
    !isIGMV1 &&
    !closed &&
    !hasAccepted &&
    !hasRejected &&
    (proposedActionIndex !== -1 ||
      (issue.resolutions.length > 0 && rejectedActionIndex === -1) ||
      issue.lastAction === 'RESOLUTION_PROPOSED');

  const resolutionRejected = hasRejected;

  // IGM 1.0 proceeds from the seller's RESOLVED action to buyer feedback/closure.
  // IGM 2.0 requires an explicit customer acceptance before that step.
  const isResolved = sellerResolved && (isIGMV1 || hasAccepted);
  const v1SellerResolved = isIGMV1 && sellerResolved;

  const displayStatus = closed
    ? 'CLOSED'
    : isResolved
      ? 'RESOLVED'
      : status === 'RESOLVED' && !hasAccepted
        ? 'PROCESSING'
        : status;

  const resolutions = issue.resolutions;
  const hasInfoOrImages = !!info.trim() || images.length > 0;

  return (
    <Screen title="Issue" onBack={back}>
      <div className="wb-col wb-gap-16">
        <div className="wb-row wb-between wb-gap-8" style={{ alignItems: 'flex-start' }}>
          <div className="wb-col wb-gap-4">
            <span className="wb-eyebrow">{supportLabel}</span>
            <h1 className="wb-h2">Issue</h1>
          </div>
          <div className="wb-row wb-gap-6" style={{ flexShrink: 0 }}>
            <Badge variant={issue.level === 'GRIEVANCE' ? 'red' : 'amber'}>{issue.level}</Badge>
            <Badge variant={isResolved ? 'green' : displayStatus === 'OPEN' ? 'default' : 'amber'}>
              {displayStatus}
            </Badge>
          </div>
        </div>

        {issue.lastAction && (
          <div
            className="wb-card wb-card--pad wb-small"
            style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
          >
            Latest update: <span className="wb-strong">{igmActionLabel(issue.lastAction)}</span>
          </div>
        )}

        <div className="wb-card wb-card--pad">
          <p className="wb-body">{issue.description}</p>
          {issue.category && (
            <p className="wb-tiny" style={{ marginTop: 8 }}>
              Category: {issue.category}
            </p>
          )}
          <p className="wb-tiny" style={{ marginTop: 2 }}>
            Raised {formatDateTime(issue.createdAt)}
          </p>
        </div>

        {issue.actions.length > 0 && (
          <div className="wb-card wb-card--pad wb-col wb-gap-8">
            <p className="wb-strong">Issue activity</p>
            {issue.actions.map((action, i) => (
              <div key={action.id || `${action.code}-${i}`} className="wb-inset" style={{ padding: 10 }}>
                <p className="wb-small wb-strong">{igmActionLabel(action.code)}</p>
                {action.shortDesc && action.shortDesc !== igmActionLabel(action.code) && (
                  <p className="wb-tiny">{action.shortDesc}</p>
                )}
                {action.createdAt && <p className="wb-tiny">{formatDateTime(action.createdAt)}</p>}
              </div>
            ))}
          </div>
        )}

        {issue.lastAction === 'INFO_REQUESTED' && !closed && (
          <div
            className="wb-card wb-card--pad wb-small"
            style={{ color: 'var(--honey-700)', background: 'var(--honey-wash-2)' }}
          >
            The operator has requested more information. Please attach the requested details or photos below to help resolve your issue.
          </div>
        )}

        {(resolutions.length > 0 || resolutionPending || resolutionRejected || hasAccepted || v1SellerResolved) && (
          <div className="wb-card wb-card--pad wb-col wb-gap-8">
            <div className="wb-row wb-between wb-gap-8" style={{ alignItems: 'center' }}>
              <p className="wb-strong" style={{ margin: 0 }}>
                {v1SellerResolved
                  ? 'Resolution from operator'
                  : closed || hasAccepted
                    ? 'Accepted resolution'
                    : resolutionRejected
                      ? 'Rejected resolution'
                      : resolutions.length > 1
                        ? 'Proposed resolutions'
                        : 'Proposed resolution'}
              </p>
              {hasAccepted && !closed && <Badge variant="green">Accepted</Badge>}
              {resolutionRejected && !closed && <Badge variant="red">Rejected</Badge>}
              {resolutionPending && !closed && <Badge variant="amber">Action required</Badge>}
            </div>

            {resolutions.length === 0 ? (
              <div className="wb-inset" style={{ padding: 12 }}>
                <p className="wb-body wb-strong" style={{ margin: 0 }}>
                  {v1SellerResolved
                    ? 'The operator has resolved this issue.'
                    : hasAccepted
                      ? 'Resolution accepted'
                      : resolutionRejected
                        ? 'Resolution proposal rejected'
                        : 'Resolution proposed by operator'}
                </p>
                <p className="wb-small" style={{ marginTop: 6 }}>
                  {v1SellerResolved
                    ? closed
                      ? 'The resolution details received from the operator are shown here. This issue is closed.'
                      : 'Review the operator’s resolution and provide your feedback below to close this issue.'
                    : hasAccepted
                      ? 'You have accepted the proposed resolution. Please submit your feedback rating below to close the issue.'
                      : resolutionRejected
                        ? 'You have rejected the proposed resolution. Waiting for the operator to review your response and follow up.'
                        : 'The operator has proposed a resolution. Please review and choose whether to accept or reject below.'}
                </p>
              </div>
            ) : (
              resolutions.map((r, i) => {
                const action = r.actionTriggered || r.code;
                const refund = formatIgmRefundAmount(r.refundAmount);
                const provider = igmProviderLabel(r.resolutionProvider);
                return (
                  <div key={r.id || i} className="wb-inset" style={{ padding: 12 }}>
                    <div className="wb-row wb-between wb-gap-8" style={{ alignItems: 'flex-start' }}>
                      <p className="wb-body wb-strong" style={{ margin: 0 }}>
                        {r.shortDesc || igmResolutionActionLabel(action)}
                      </p>
                      {action && (
                        <Badge variant="default">{igmResolutionActionLabel(action)}</Badge>
                      )}
                    </div>
                    {r.longDesc && r.longDesc !== r.shortDesc && (
                      <p className="wb-small" style={{ marginTop: 6 }}>
                        {r.longDesc}
                      </p>
                    )}
                    <div className="wb-col wb-gap-4" style={{ marginTop: 8 }}>
                      {refund && (
                        <p className="wb-small">
                          Refund amount: <span className="wb-strong">{refund}</span>
                        </p>
                      )}
                      {provider && (
                        <p className="wb-tiny wb-ink3">Offered by {provider}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {resolutionPending && (
              <div className="wb-col wb-gap-12" style={{ paddingTop: 4 }}>
                <div className="wb-row wb-wrap wb-gap-8">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => resolveMut.mutate({ accepted: true })}
                    disabled={resolveMut.isPending}
                  >
                    {resolveMut.isPending ? 'Accepting…' : 'Accept resolution'}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowRejectPanel((cur) => !cur)}
                    disabled={resolveMut.isPending}
                  >
                    {showRejectPanel ? 'Hide rejection details' : 'Reject resolution'}
                  </Button>
                </div>

                {showRejectPanel && (
                  <div
                    className="wb-card wb-card--pad wb-col wb-gap-12"
                    style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }}
                  >
                    <p className="wb-strong" style={{ margin: 0 }}>
                      Rejection details
                    </p>
                    <p className="wb-small wb-ink2" style={{ margin: 0 }}>
                      Please provide a reason and optional screenshots explaining why you are rejecting this resolution.
                    </p>
                    <textarea
                      id="reject-info"
                      value={rejectInfo}
                      onChange={(e) => setRejectInfo(e.target.value)}
                      rows={3}
                      placeholder="Explain why you are rejecting…"
                      className="wb-field wb-field--area"
                    />
                    <div className="wb-col wb-gap-8">
                      <p className="wb-label" style={{ margin: 0 }}>
                        Images & Screenshots (optional, up to {MAX_IMAGES})
                      </p>
                      {rejectImages.length > 0 && (
                        <div className="wb-row wb-wrap wb-gap-8">
                          {rejectImages.map((url) => (
                            <div key={url} style={{ position: 'relative' }}>
                              <img
                                src={url}
                                alt="attachment"
                                style={{
                                  height: 64,
                                  width: 64,
                                  borderRadius: 'var(--r-sm)',
                                  border: '1px solid var(--line)',
                                  objectFit: 'cover',
                                }}
                              />
                              <button
                                type="button"
                                aria-label="Remove image"
                                onClick={() => setRejectImages((cur) => cur.filter((u) => u !== url))}
                                style={{
                                  position: 'absolute',
                                  top: -6,
                                  right: -6,
                                  width: 20,
                                  height: 20,
                                  borderRadius: 999,
                                  border: 'none',
                                  cursor: 'pointer',
                                  background: 'var(--ink)',
                                  color: 'var(--paper)',
                                  fontSize: 12,
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {rejectImages.length < MAX_IMAGES && (
                        <div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => rejectFileRef.current?.click()}
                            disabled={rejectUploading}
                          >
                            {rejectUploading ? 'Uploading…' : 'Add image from device'}
                          </Button>
                        </div>
                      )}
                      <input
                        ref={rejectFileRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        onChange={onPickRejectFiles}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className="wb-row wb-gap-8" style={{ marginTop: 8 }}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() =>
                          resolveMut.mutate({
                            accepted: false,
                            images: rejectImages,
                            comment: rejectInfo,
                          })
                        }
                        disabled={
                          (!rejectInfo.trim() && rejectImages.length === 0) ||
                          rejectUploading ||
                          resolveMut.isPending
                        }
                      >
                        {resolveMut.isPending ? 'Rejecting…' : 'Submit rejection'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowRejectPanel(false);
                          setRejectInfo('');
                          setRejectImages([]);
                        }}
                        disabled={resolveMut.isPending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {resolutionRejected && !closed && (
          <div
            className="wb-card wb-card--pad wb-small wb-col wb-gap-8"
            style={{
              color: 'var(--danger-700, #b91c1c)',
              background: 'var(--danger-wash, #fef2f2)',
              border: '1px solid var(--danger-border, #fecaca)',
            }}
          >
            <p className="wb-strong" style={{ margin: 0 }}>
              Resolution rejected
            </p>
            <p style={{ margin: 0 }}>
              You have rejected the proposed resolution. The operator will review your response and follow up.
            </p>
          </div>
        )}

        {(hasAccepted || v1SellerResolved) && !closed && (
          <div
            className="wb-card wb-card--pad wb-col wb-gap-12"
            style={{ border: '1px solid var(--emerald-300, #86efac)' }}
          >
            <div>
              <p className="wb-strong" style={{ color: 'var(--emerald-800, #166534)' }}>
                {v1SellerResolved ? 'Resolution provided' : 'Resolution accepted'}
              </p>
              <p className="wb-small wb-ink2" style={{ marginTop: 4 }}>
                Please provide your feedback rating below to complete and close this issue.
              </p>
            </div>
            <div className="wb-row wb-gap-8">
              <Button
                variant="primary"
                size="sm"
                icon="thumbUp"
                onClick={() => closeMut.mutate('THUMBS-UP')}
                disabled={closeMut.isPending}
              >
                Resolved (Satisfied)
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon="thumbDown"
                onClick={() => closeMut.mutate('THUMBS-DOWN')}
                disabled={closeMut.isPending}
              >
                Not satisfied
              </Button>
            </div>
          </div>
        )}

        {actionError && (
          <p className="wb-small" style={{ color: 'var(--danger)' }}>
            {actionError}
          </p>
        )}
        {actionNote && !actionError && (
          <p
            className="wb-small"
            style={{
              background: 'var(--surface-2)',
              borderRadius: 'var(--r-md)',
              padding: '10px 14px',
            }}
          >
            {actionNote}
          </p>
        )}

        {/* Regular information addition is enabled only during ongoing investigation, not when a resolution decision is pending or rejected */}
        {!isIGMV1 && !closed && !isResolved && !hasAccepted && !resolutionPending && !resolutionRejected && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <div>
              <label className="wb-label" htmlFor="add-info">
                Add information
              </label>
              <textarea
                id="add-info"
                value={info}
                onChange={(e) => setInfo(e.target.value)}
                rows={3}
                placeholder="Add details for the operator…"
                className="wb-field wb-field--area"
              />
            </div>

            <div className="wb-col wb-gap-8">
              <p className="wb-label" style={{ margin: 0 }}>
                Images & Screenshots <span className="wb-ink3">(optional, up to {MAX_IMAGES})</span>
              </p>
              {images.length > 0 && (
                <div className="wb-row wb-wrap wb-gap-8">
                  {images.map((url, i) => (
                    <div key={url} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Attachment ${i + 1}`}
                        style={{
                          height: 64,
                          width: 64,
                          borderRadius: 'var(--r-sm)',
                          border: '1px solid var(--line)',
                          objectFit: 'cover',
                        }}
                      />
                      <button
                        type="button"
                        aria-label="Remove image"
                        onClick={() => setImages((cur) => cur.filter((u) => u !== url))}
                        style={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          width: 20,
                          height: 20,
                          borderRadius: 999,
                          border: 'none',
                          cursor: 'pointer',
                          background: 'var(--ink)',
                          color: 'var(--paper)',
                          fontSize: 12,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {images.length < MAX_IMAGES && (
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="plus"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading…' : 'Add image from device'}
                  </Button>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                multiple
                onChange={onPickFiles}
                style={{ display: 'none' }}
              />
            </div>

            <div className="wb-row wb-wrap wb-gap-8">
              <Button
                variant="primary"
                size="sm"
                onClick={() => updateMut.mutate()}
                disabled={!hasInfoOrImages || uploading || updateMut.isPending}
              >
                {updateMut.isPending ? 'Sending…' : uploading ? 'Uploading…' : 'Add info'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon="escalate"
                onClick={() => escalateMut.mutate()}
                disabled={escalateMut.isPending || issue.level === 'GRIEVANCE'}
              >
                Escalate
              </Button>
            </div>

            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <p className="wb-small" style={{ marginBottom: 8 }}>
                Close this issue
              </p>
              <div className="wb-row wb-gap-8">
                <Button
                  variant="secondary"
                  size="sm"
                  icon="thumbUp"
                  onClick={() => closeMut.mutate('THUMBS-UP')}
                  disabled={closeMut.isPending}
                >
                  Resolved
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon="thumbDown"
                  onClick={() => closeMut.mutate('THUMBS-DOWN')}
                  disabled={closeMut.isPending}
                >
                  Not resolved
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Assistance / Escalation options when rejection is active */}
        {resolutionRejected && !closed && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <div>
              <p className="wb-strong" style={{ margin: 0 }}>Need further assistance?</p>
              <p className="wb-small wb-ink2" style={{ marginTop: 4, marginBottom: 0 }}>
                If you need immediate intervention, you can escalate this issue to a Grievance Officer or close the ticket.
              </p>
            </div>
            <div className="wb-row wb-wrap wb-gap-8">
              {issue.level !== 'GRIEVANCE' && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon="escalate"
                  onClick={() => escalateMut.mutate()}
                  disabled={escalateMut.isPending}
                >
                  {escalateMut.isPending ? 'Escalating…' : 'Escalate to Grievance'}
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                icon="thumbDown"
                onClick={() => closeMut.mutate('THUMBS-DOWN')}
                disabled={closeMut.isPending}
              >
                Close ticket
              </Button>
            </div>
          </div>
        )}
      </div>
    </Screen>
  );
}
